import React, { useCallback, useState } from 'react';

import {
  useInvalidatePositionQueries,
  useWalletWithPositionsQuery,
} from '#/api/queries/walletPositionsQueries';
import { useConfirmationModal } from '#/components/basic/modal/ConfirmationModal';
import ClosePositionModal from '#/components/trading/BuySellPanel/ClosePositionModal';
import { closePosition } from '#/components/trading/BuySellPanel/orderExecute';
import { waitAsync } from '#/lib/promiseTools';
import { useSelector } from '#/reduxState/store';

export function useCloseAllPositions(marketFilter?: string | false) {
  const wallet = useSelector(state => state.wallet);
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  const takerOrderMinimums = useSelector(
    state => state.markets.listedMarkets.takerOrderMinimums
  );
  const delegatedAuthorization = useSelector(
    state => state.delegatedKey.authorization
  );

  const { data: walletWithPositions } = useWalletWithPositionsQuery();
  const invalidatePositionQueries = useInvalidatePositionQueries();

  const [closeAllModalPlaceholder, showCloseAllConfirmation] =
    useConfirmationModal({
      title: 'Close All Positions',
      message: 'All positions will be closed.',
      confirmText: 'Close All Positions',
      confirmButtonProps: { bordered: true, color: 'red', autoFocus: true },
      onConfirm: async () => {
        const filteredPositions = marketFilter
          ? walletWithPositions?.positions?.filter(
              position => position.market === marketFilter
            )
          : walletWithPositions?.positions;

        // TODO: Close All positions: how we present possible errors to user ?
        //  For closing All: "Some positions cannot be closed because liquidity issues"
        const responses = await Promise.allSettled(
          filteredPositions?.map(async (position, index) => {
            const stepSize = stepSizes[position.market];
            if (!stepSize) {
              return Promise.reject(
                new Error(`Position ${position.market} missing stepSize`)
              );
            }
            // We want to trigger at most 10 request each second,
            // so to be sage, we limit close to 5 request each 0.8s
            const delayMultiplier = Math.floor(index / 5);
            await waitAsync(delayMultiplier * 800);

            const takerOrderMinimum = takerOrderMinimums[position.market];
            if (!takerOrderMinimum) {
              throw new Error(
                `Position takerOrderMinimum ${position.market} not available`
              );
            }
            return closePosition({
              delegatedWallet: delegatedAuthorization,
              position,
              stepSizeDecimals: stepSize.visibleDecimals,
              wallet,
              takerOrderMinimum,
            });
          }) || [Promise.reject(new Error('No positions'))]
        ).then(invalidatePositionQueries);
        console.log('Positions close response:', responses);
      },
    });
  return { closeAllModalPlaceholder, showCloseAllConfirmation };
}

export function useClosePosition(onClose?: () => void) {
  const [closeBy, setCloseBy] = useState<'limit' | 'market'>('limit');
  const [market, setMarket] = useState<string>();

  const showClosePositionModal = useCallback(
    (market: string) => setMarket(market),
    []
  );

  return {
    closeModalPlaceholder: market ? (
      <ClosePositionModal
        market={market}
        closeBy={closeBy}
        onChangeCloseBy={setCloseBy}
        onModalClose={() => {
          setMarket(undefined);
          onClose?.();
        }}
      />
    ) : null,
    showClosePositionModal,
  };
}
